import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, ReferenceLine
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

const ttStyle = {
  backgroundColor: '#1a2744', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px', color: '#e2e8f0', fontSize: 12,
  fontFamily: 'Plus Jakarta Sans, sans-serif'
};

// Pasirinktinis Tooltip su šventės žyma
function CustomTooltip({ active, payload, label, holidays }) {
  if (!active || !payload?.length) return null;
  const dateStr = label?.slice(0, 10);
  const holiday = holidays?.find(h => h.date === dateStr);
  const isWeekend = holiday && ['Šeštadienis','Sekmadienis'].includes(holiday.name);
  return (
    <div style={{ ...ttStyle, padding:'10px 14px', minWidth:160 }}>
      <div style={{ fontWeight:700, marginBottom:6, fontSize:13 }}>{label}</div>
      {holiday && (
        <div style={{ fontSize:11, marginBottom:6, padding:'3px 8px', borderRadius:4,
          background: isWeekend ? 'rgba(139,92,246,0.2)' : 'rgba(245,158,11,0.2)',
          color: isWeekend ? '#a78bfa' : '#fbbf24' }}>
          {isWeekend ? '🗓️' : '🎉'} {holiday.name}
        </div>
      )}
      {payload.map((p, i) => (
        <div key={i} style={{ display:'flex', justifyContent:'space-between', gap:16, fontSize:12, color: p.color }}>
          <span>{p.name}</span><span style={{ fontWeight:700 }}>{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

// Surasti šventines ir savaitgalio datas duomenyse
function getHolidayAreas(data, holidays) {
  if (!holidays?.length || !data?.length) return [];
  return data
    .map(row => {
      const periodStr = row.period || '';
      // Match full date (2026-01-15) or month (2026-01)
      const matchingHols = holidays.filter(hol => hol.date?.startsWith(periodStr.slice(0,7)) || periodStr.startsWith(hol.date?.slice(0,10)));
      if (!matchingHols.length) return null;
      // Prefer holiday over weekend
      const h = matchingHols.find(h => !['Šeštadienis','Sekmadienis'].includes(h.name)) || matchingHols[0];
      const isWeekend = ['Šeštadienis','Sekmadienis'].includes(h.name);
      return { period: row.period, name: h.name, isWeekend };
    })
    .filter(Boolean);
}

export function TimeSeriesChart({ data, title, type = 'area', holidays = [] }) {
  if (!data?.length) return <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>Nėra duomenų</div>;
  const Comp = type === 'area' ? AreaChart : BarChart;
  const areas = getHolidayAreas(data, holidays);

  return (
    <div className="chart-container">
      <div className="chart-title">
        {title}
        {holidays.length > 0 && (
          <span style={{ marginLeft:12, fontSize:11, fontWeight:400, color:'var(--text-muted)' }}>
            <span style={{ color:'#fbbf24' }}>█</span> Šventė &nbsp;
            <span style={{ color:'#a78bfa' }}>█</span> Savaitgalis
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <Comp data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id="gH" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="period" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => v?.slice(5)} />
          <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
          <Tooltip content={<CustomTooltip holidays={holidays} />} />
          <Legend wrapperStyle={{ color: '#94a3b8', fontSize: 12 }} />

          {/* Švenčių pažymėjimai */}
          {areas.map((a, i) => (
            <ReferenceLine key={i} x={a.period}
              stroke={a.isWeekend ? '#8b5cf6' : '#f59e0b'}
              strokeWidth={a.isWeekend ? 1 : 2}
              strokeDasharray={a.isWeekend ? '3 3' : ''}
              strokeOpacity={0.6}
            />
          ))}

          {type === 'area' ? <>
            <Area type="monotone" dataKey="human" name="Žmonės"       stroke="#3b82f6" fill="url(#gH)" strokeWidth={2} dot={false} />
            <Area type="monotone" dataKey="car"   name="Automobiliai" stroke="#10b981" fill="url(#gC)" strokeWidth={2} dot={false} />
            <Area type="monotone" dataKey="bike"  name="Dviračiai"    stroke="#f59e0b" fill="none"     strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
            <Area type="monotone" dataKey="bus"   name="Autobusai"    stroke="#8b5cf6" fill="none"     strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
            <Area type="monotone" dataKey="truck" name="Sunkvežimiai" stroke="#ef4444" fill="none"     strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
          </> : <>
            <Bar dataKey="human" name="Žmonės"       fill="#3b82f6" radius={[4,4,0,0]} />
            <Bar dataKey="car"   name="Automobiliai" fill="#10b981" radius={[4,4,0,0]} />
            <Bar dataKey="bike"  name="Dviračiai"    fill="#f59e0b" radius={[4,4,0,0]} />
            <Bar dataKey="bus"   name="Autobusai"    fill="#8b5cf6" radius={[4,4,0,0]} />
            <Bar dataKey="truck" name="Sunkvežimiai" fill="#ef4444" radius={[4,4,0,0]} />
          </>}
        </Comp>
      </ResponsiveContainer>
    </div>
  );
}

export function CameraCompareChart({ data, title }) {
  if (!data?.length) return null;
  return (
    <div className="chart-container">
      <div className="chart-title">{title}</div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 90, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
          <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} />
          <YAxis type="category" dataKey="camera" tick={{ fill: '#94a3b8', fontSize: 11 }} width={90} />
          <Tooltip contentStyle={ttStyle} />
          <Legend wrapperStyle={{ color: '#94a3b8', fontSize: 12 }} />
          <Bar dataKey="human" name="Žmonės"       fill="#3b82f6" radius={[0,4,4,0]} />
          <Bar dataKey="car"   name="Automobiliai" fill="#10b981" radius={[0,4,4,0]} />
          <Bar dataKey="bike"  name="Dviračiai"    fill="#f59e0b" radius={[0,4,4,0]} />
          <Bar dataKey="truck" name="Sunkvežimiai" fill="#ef4444" radius={[0,4,4,0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function PeakHoursChart({ data, title }) {
  if (!data?.length) return null;
  const hourly = {};
  for (const row of data) {
    const h = new Date(row.period).getHours();
    if (!hourly[h]) hourly[h] = { hour: `${String(h).padStart(2,'0')}:00`, total: 0 };
    hourly[h].total += (row.human || 0) + (row.car || 0) + (row.bike || 0) + (row.bus || 0) + (row.truck || 0);
  }
  const chartData = Object.values(hourly).sort((a, b) => parseInt(a.hour) - parseInt(b.hour));
  return (
    <div className="chart-container">
      <div className="chart-title">{title}</div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="hour" tick={{ fill: '#94a3b8', fontSize: 11 }} />
          <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
          <Tooltip contentStyle={ttStyle} />
          <Bar dataKey="total" name="Iš viso" fill="#8b5cf6" radius={[4,4,0,0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function PieShareChart({ data, title }) {
  if (!data?.length) return null;
  const pieData = data.map(cam => ({ name: cam.camera, value: cam.human + cam.car + cam.bike + cam.bus + cam.truck }));
  return (
    <div className="chart-container">
      <div className="chart-title">{title}</div>
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value"
            label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
            {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip contentStyle={ttStyle} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
